/**
 * External dependencies
 */
import type { CSSProperties } from 'react';
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { _x, sprintf } from '@safe-wordpress/i18n';
import {
	Button,
	ColorIndicator,
	VStack,
	DropdownContentWrapper,
	Dropdown,
	type WordPressComponentProps,
	type ColorObject,
	type DropdownProps as DropdownComponentProps,
} from '@safe-wordpress/components';

/**
 * Internal dependencies
 */
import { contextConnect } from '../../context';
import BorderControlStylePicker from '../border-control-style-picker';
import { useBorderControlDropdown } from './hook';
import ColorPalette from '../../color-palette';
import { isMultiplePaletteArray } from '../../color-palette/utils';
import type { ColorProps, DropdownProps } from '../types';

const getAriaLabelColorValue = ( colorValue: string ) => {
	// Leave hex values as-is. Remove the `var()` wrapper from CSS vars.
	return colorValue.replace( /^var\((.+)\)$/, '$1' );
};

const getColorObject = (
	colorValue: CSSProperties[ 'borderColor' ],
	colors: ColorProps[ 'colors' ] | undefined
) => {
	if ( ! colorValue || ! colors ) {
		return;
	}

	if ( isMultiplePaletteArray( colors ) ) {
		// Multiple origins
		let matchedColor;

		colors.some( ( origin ) =>
			origin.colors.some( ( color ) => {
				if ( color.color === colorValue ) {
					matchedColor = color;
					return true;
				}

				return false;
			} )
		);

		return matchedColor;
	}

	// Single origin
	return colors.find( ( color ) => color.color === colorValue );
};

const getToggleAriaLabel = (
	colorValue: CSSProperties[ 'borderColor' ],
	colorObject: ColorObject | undefined,
	style: CSSProperties[ 'borderStyle' ],
	isStyleEnabled: boolean
) => {
	if ( isStyleEnabled ) {
		if ( colorObject ) {
			const ariaLabelValue = getAriaLabelColorValue( colorObject.color );
			return style
				? sprintf(
						/* translators: %1$s: The name of the color e.g. "vivid red". %2$s: The color's hex code e.g.: "#f00". %3$s: The current border style selection e.g. "solid". */
						_x(
							'Border color and style picker. The currently selected color is called "%1$s" and has a value of "%2$s". The currently selected style is "%3$s".',
							'text',
							'nelio-ab-testing'
						),
						colorObject.name,
						ariaLabelValue,
						style
				  )
				: sprintf(
						/* translators: %1$s: The name of the color e.g. "vivid red". %2$s: The color's hex code e.g.: "#f00". */
						_x(
							'Border color and style picker. The currently selected color is called "%1$s" and has a value of "%2$s".',
							'text',
							'nelio-ab-testing'
						),
						colorObject.name,
						ariaLabelValue
				  );
		}

		if ( colorValue ) {
			const ariaLabelValue = getAriaLabelColorValue( colorValue );
			return style
				? sprintf(
						/* translators: %1$s: The color's hex code e.g.: "#f00". %2$s: The current border style selection e.g. "solid". */
						_x(
							'Border color and style picker. The currently selected color has a value of "%1$s". The currently selected style is "%2$s".',
							'text',
							'nelio-ab-testing'
						),
						ariaLabelValue,
						style
				  )
				: sprintf(
						/* translators: %s: The color's hex code e.g: "#f00". */
						_x(
							'Border color and style picker. The currently selected color has a value of "%s".',
							'text',
							'nelio-ab-testing'
						),
						ariaLabelValue
				  );
		}

		return _x(
			'Border color and style picker.',
			'text',
			'nelio-ab-testing'
		);
	}

	if ( colorObject ) {
		return sprintf(
			/* translators: %1$s: The name of the color e.g. "vivid red". %2$s: The color's hex code e.g: "#f00". */
			_x(
				'Border color picker. The currently selected color is called "%1$s" and has a value of "%2$s".',
				'text',
				'nelio-ab-testing'
			),
			colorObject.name,
			getAriaLabelColorValue( colorObject.color )
		);
	}

	if ( colorValue ) {
		return sprintf(
			/* translators: %s: The color's hex code e.g: "#f00". */
			_x(
				'Border color picker. The currently selected color has a value of "%s".',
				'text',
				'nelio-ab-testing'
			),
			getAriaLabelColorValue( colorValue )
		);
	}

	return _x( 'Border color picker.', 'text', 'nelio-ab-testing' );
};

const BorderControlDropdown = (
	props: WordPressComponentProps< DropdownProps, 'div' >,
	forwardedRef: React.ForwardedRef< unknown >
) => {
	const {
		__experimentalIsRenderedInSidebar,
		border,
		colors,
		disableCustomColors,
		disableColorPicker,
		disableStylePicker,
		enableAlpha,
		enableStyle,
		indicatorClassName,
		indicatorWrapperClassName,
		isStyleSettable,
		onReset,
		onColorChange,
		onStyleChange,
		popoverContentClassName,
		popoverControlsClassName,
		resetButtonWrapperClassName,
		size,
		__unstablePopoverProps,
		...otherProps
	} = useBorderControlDropdown( props );

	const { color, style } = border || {};
	const colorObject = getColorObject( color, colors );

	const toggleAriaLabel = getToggleAriaLabel(
		color,
		colorObject,
		style,
		enableStyle
	);

	const enableResetButton = color || ( style && style !== 'none' );
	const dropdownPosition = __experimentalIsRenderedInSidebar
		? 'bottom left'
		: undefined;

	const renderToggle: DropdownComponentProps[ 'renderToggle' ] = ( {
		onToggle,
	} ) => (
		<Button
			onClick={ onToggle }
			className={ clsx( {
				'nab-components--disabled':
					disableColorPicker && disableStylePicker,
			} ) }
			variant="tertiary"
			aria-label={ toggleAriaLabel }
			tooltipPosition={ dropdownPosition }
			disabled={ disableColorPicker && disableStylePicker }
			label={ _x(
				'Border color and style picker',
				'text',
				'nelio-ab-testing'
			) }
			showTooltip
			__next40pxDefaultSize={ size === '__unstable-large' }
		>
			<span className={ indicatorWrapperClassName }>
				<ColorIndicator
					className={ indicatorClassName }
					colorValue={ color }
				/>
			</span>
		</Button>
	);

	const renderContent: DropdownComponentProps[ 'renderContent' ] = () => (
		<>
			<DropdownContentWrapper paddingSize="medium">
				<VStack className={ popoverControlsClassName } spacing={ 6 }>
					<ColorPalette
						className={ clsx( popoverContentClassName, {
							'nab-components--disabled': disableColorPicker,
						} ) }
						value={ color }
						onChange={ onColorChange }
						__experimentalIsRenderedInSidebar={
							__experimentalIsRenderedInSidebar
						}
						clearable={ false }
						enableAlpha={ enableAlpha }
						disabled={ disableColorPicker }
						{ ...( disableColorPicker
							? {}
							: { colors, disableCustomColors } ) }
					/>
					{ enableStyle && isStyleSettable && (
						<BorderControlStylePicker
							label={ _x( 'Style', 'text', 'nelio-ab-testing' ) }
							value={ style }
							onChange={ onStyleChange }
							disabled={ disableStylePicker }
						/>
					) }
				</VStack>
				<div className={ resetButtonWrapperClassName }>
					<Button
						variant="tertiary"
						onClick={ () => {
							onReset();
						} }
						disabled={ ! enableResetButton }
						__next40pxDefaultSize
					>
						{ _x( 'Reset', 'text', 'nelio-ab-testing' ) }
					</Button>
				</div>
			</DropdownContentWrapper>
		</>
	);

	return (
		<Dropdown
			disabled={ disableColorPicker && disableStylePicker }
			renderToggle={ renderToggle }
			renderContent={ renderContent }
			popoverProps={ {
				...__unstablePopoverProps,
			} }
			{ ...otherProps }
			ref={ forwardedRef }
		/>
	);
};

const ConnectedBorderControlDropdown = contextConnect(
	BorderControlDropdown,
	'BorderControlDropdown'
);

export default ConnectedBorderControlDropdown;
